// ref: docs/CODEMAPS/PATTERNS.md §4.3 Hooks — Extracting UI Logic
import { SizeType } from 'antd/es/config-provider/SizeContext';
import _ from 'lodash';
import { useMemo } from 'react';
import { getAntdSize } from '../../autoComplete/helper';
import type { IProps } from './types';

/**
 * Reports whether a country's phone numbers should be rendered in dashed 3-3-4 form.
 *
 * @param country - Country code to check.
 * @returns True when the country is US or CA.
 */
const checkToFormatPhoneNumber = (country: any) => {
  const shouldFormatPhoneNumberCountries = ['US', 'CA'];
  return _.includes(shouldFormatPhoneNumberCountries, country);
};

/**
 * Backs the phone input's country selector and digit-only number field. Clones the country options, strips non-digits and caps the number at ten digits for countries that require formatting, memoizes the Ant Design size from the size prop, and resolves the selected country as the raw countryValue when isObjectCountry or the matching option otherwise.
 *
 * @param props - Field name, country options and value, object-country flag, size and the change callbacks.
 * @returns The country options, resolved size, selected country and the number and country change handlers.
 */
export const usePhoneInput = (
  props: Pick<
    IProps,
    | 'name'
    | 'countryOptions'
    | 'countryValue'
    | 'isObjectCountry'
    | 'size'
    | 'onChange'
    | 'onChangeSelect'
  >,
) => {
  const { name = '' } = props;
  const countryData = _.clone(props.countryOptions);

  /**
   * Change handler for the phone number field that keeps only digits. Truncates to ten digits and reports early when the selected country requires phone formatting, otherwise forwards the field name and the digits-only value to props.onChange.
   *
   * @param e - The phone input change event.
   * @returns Nothing.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input: string = e.target.value;
    const formattedInput: string = input.replace(/\D/g, '');
    if (checkToFormatPhoneNumber(props.countryValue) && formattedInput) {
      props.onChange?.(name, formattedInput.slice(0, 10));
      return;
    }
    if (props.onChange) {
      props.onChange(name, formattedInput);
    }
  };

  /**
   * Handles selection of a country in the phone input. Forwards the field name and selected value to props.onChangeSelect when defined.
   *
   * @param value - The selected country value.
   * @returns Nothing.
   */
  const onChangeSelect = (value: string) => {
    if (props.onChangeSelect) {
      props.onChangeSelect(name, value);
    }
  };

  /**
   * Memoized Ant Design size derived from the component's size prop. Recomputes getAntdSize(props.size) only when props.size changes.
   *
   * @returns The resolved Ant Design size.
   */
  const antSize = useMemo<ReturnType<typeof getAntdSize>>(
    () => getAntdSize(props.size),
    [props.size],
  );

  /**
   * Memoized selected-country value for the phone input. Returns the raw countryValue when isObjectCountry, otherwise the country option whose value matches countryValue.
   *
   * @returns The resolved selected country option or value.
   */
  const selectedValue = useMemo(() => {
    if (props.isObjectCountry) {
      return props.countryValue;
    }
    return _.find(countryData, (x) => x?.value === props.countryValue);
  }, [props.countryValue, countryData, props.isObjectCountry]);

  return { countryData, antSize: antSize as SizeType, selectedValue, handleChange, onChangeSelect };
};
